import React, { useMemo, useState } from "react";
import { Check } from "lucide-react";
import { useT, Namespace, TranslationKey } from "@racoon/i18n";
import { Button, Checkbox, SearchablePopover, DataTable, cn } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { useListContainerTypesQuery } from "../../../../shared/api/container-type/container-type.grpc";
import type { Column } from "@racoon/ui";
import { IcPlus } from "@racoon/icons";

import { matchesSearch } from "@racoon/lib";
import { foreignOwnerName } from "~/shared/lib/holding-type";
interface CapabilitiesMatrixProps {
  ownerOrganizationId: number | null;
  selectedJobTypes: ConnectProto.JobType[];
  setSelectedJobTypes: React.Dispatch<React.SetStateAction<ConnectProto.JobType[]>>;
  selectedContainerTypeIds: number[];
  setSelectedContainerTypeIds: React.Dispatch<React.SetStateAction<number[]>>;
  capabilitiesMatrix: Record<number, Record<number, boolean>>;
  setCapabilitiesMatrix: React.Dispatch<
    React.SetStateAction<Record<number, Record<number, boolean>>>
  >;
}

const MatrixContext = React.createContext<any>(null);

const MatrixCheckbox = ({ ctIdNum, jt }: any) => {
  const { capabilitiesMatrix, setCapabilitiesMatrix } = React.useContext(MatrixContext);
  return (
    <div className="flex justify-center w-full">
      <Checkbox
        className="!rounded-full data-[state=unchecked]:bg-[#D9D9D9] data-[state=unchecked]:border-transparent data-[state=checked]:!bg-brand-blue"
        checked={!!capabilitiesMatrix[ctIdNum]?.[jt]}
        onCheckedChange={(checked) => {
          setCapabilitiesMatrix((prev: any) => ({
            ...prev,
            [ctIdNum]: {
              ...(prev[ctIdNum] || {}),
              [jt]: !!checked,
            },
          }));
        }}
      />
    </div>
  );
};

/**
 * One option of the add popovers. Multiselect: a click toggles it and the panel
 * stays open for the next one.
 */
const OptionRow: React.FC<{
  selected: boolean;
  onToggle: () => void;
  className?: string;
  children: React.ReactNode;
}> = ({ selected, onToggle, className, children }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={selected}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onToggle();
    }}
    className={cn(
      "flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-blue-50",
      className,
    )}
  >
    <span
      className={cn(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
        selected ? "border-blue-600 bg-blue-600" : "border-slate-300 bg-slate-200",
      )}
    >
      {selected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
    </span>
    <span className="min-w-0 truncate">{children}</span>
  </button>
);

/**
 * "Select all" over the options the search leaves: adds every one of them, or
 * takes them all off once they are all in.
 */
const toggleAll = <T,>(prev: T[], options: T[]): T[] => {
  const allIn = options.every((option) => prev.includes(option));
  return allIn
    ? prev.filter((value) => !options.includes(value))
    : [...prev, ...options.filter((option) => !prev.includes(option))];
};

/** A job column's header: ticking it marks the job for every container in the matrix. */
const JobColumnHeader = ({ jt, label }: { jt: ConnectProto.JobType; label: string }) => {
  const { capabilitiesMatrix, setCapabilitiesMatrix, selectedContainerTypeIds } =
    React.useContext(MatrixContext);
  const ids: number[] = selectedContainerTypeIds;
  const allChecked = ids.length > 0 && ids.every((id) => !!capabilitiesMatrix[id]?.[jt]);
  return (
    <span className="flex items-center justify-center gap-2">
      <Checkbox
        className="!rounded-full data-[state=unchecked]:bg-[#D9D9D9] data-[state=unchecked]:border-transparent data-[state=checked]:!bg-brand-blue"
        checked={allChecked}
        disabled={ids.length === 0}
        aria-label={label}
        onCheckedChange={(checked) =>
          setCapabilitiesMatrix((prev: any) => {
            const next = { ...prev };
            for (const id of ids) next[id] = { ...(prev[id] || {}), [jt]: !!checked };
            return next;
          })
        }
      />
      <span className="uppercase text-xs font-bold text-text-primary">{label}</span>
    </span>
  );
};

const AddContainerCell = ({ t }: any) => {
  const { containerTypes, selectedContainerTypeIds, setSelectedContainerTypeIds } =
    React.useContext(MatrixContext);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Every type is listed, the ones already in the matrix ticked: unticking one
  // takes its row out again.
  const filteredContainerTypes = useMemo(() => {
    const all = containerTypes || [];
    if (!search) return all;
    return all.filter((ct: any) => matchesSearch(ct.name || ct.volume?.toString() || "", search));
  }, [containerTypes, search]);
  const filteredIds = filteredContainerTypes.map((ct: any) => Number(ct.id));

  return (
    <SearchablePopover
      open={open}
      onOpenChange={setOpen}
      searchValue={search}
      onSearchChange={setSearch}
      inputId="add-container-type-search"
      placeholder={t(Namespace.Settings, "search" as TranslationKey)}
      trigger={
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-text-primary font-medium px-0 hover:bg-transparent"
        >
          <IcPlus className="text-brand-blue w-6 h-6 " />

          {t(Namespace.Settings, "truckType.dialog.addContainer" as TranslationKey)}
        </Button>
      }
    >
      {filteredIds.length > 0 && (
        <OptionRow
          selected={filteredIds.every((id: number) => selectedContainerTypeIds.includes(id))}
          onToggle={() =>
            setSelectedContainerTypeIds((prev: number[]) => toggleAll(prev, filteredIds))
          }
          className="font-medium"
        >
          {t(Namespace.Settings, "truckType.dialog.selectAll" as TranslationKey)}
        </OptionRow>
      )}
      {filteredContainerTypes.map((ct: any) => {
        const id = Number(ct.id);
        return (
          <OptionRow
            key={ct.id}
            selected={selectedContainerTypeIds.includes(id)}
            onToggle={() =>
              setSelectedContainerTypeIds((prev: number[]) =>
                prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
              )
            }
          >
            {ct.name || ct.volume?.toString()}
          </OptionRow>
        );
      })}
    </SearchablePopover>
  );
};

// Registered as the column's header component itself, not wrapped in an inline
// renderer: a renderer recreated on every selection would remount it, closing
// the picker after each click. So the selection comes from the context.
const AddJobHeader = () => {
  const t = useT();
  const { selectedJobTypes, setSelectedJobTypes } = React.useContext(MatrixContext);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const availableJobTypes = useMemo(() => {
    return (
      Object.values(ConnectProto.JobType).filter(
        (v) => typeof v === "number",
      ) as ConnectProto.JobType[]
    ).filter((v) => v !== 0);
  }, []);

  const filteredJobTypes = useMemo(() => {
    if (!search) return availableJobTypes;
    return availableJobTypes.filter((jt) =>
      t(Namespace.Enum, `JOB_TYPE_${ConnectProto.JobType[jt]}` as TranslationKey)
        .toLowerCase()
        .includes(search.toLowerCase()),
    );
  }, [availableJobTypes, search, t]);

  return (
    <SearchablePopover
      open={open}
      onOpenChange={setOpen}
      searchValue={search}
      onSearchChange={setSearch}
      inputId="add-job-type-search"
      placeholder={t(Namespace.Settings, "search" as TranslationKey)}
      trigger={
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-text-primary font-medium px-0 hover:bg-transparent"
        >
          <IcPlus className="text-brand-blue w-6 h-6 " />

          {t(Namespace.Settings, "truckType.dialog.addJob" as TranslationKey)}
        </Button>
      }
    >
      {filteredJobTypes.length > 0 && (
        <OptionRow
          selected={filteredJobTypes.every((jt) => selectedJobTypes.includes(jt))}
          onToggle={() =>
            setSelectedJobTypes((prev: ConnectProto.JobType[]) => toggleAll(prev, filteredJobTypes))
          }
          className="font-medium"
        >
          {t(Namespace.Settings, "truckType.dialog.selectAll" as TranslationKey)}
        </OptionRow>
      )}
      {filteredJobTypes.map((jt) => (
        <OptionRow
          key={jt}
          selected={selectedJobTypes.includes(jt)}
          onToggle={() =>
            setSelectedJobTypes((prev: ConnectProto.JobType[]) =>
              prev.includes(jt) ? prev.filter((value) => value !== jt) : [...prev, jt],
            )
          }
        >
          {t(Namespace.Enum, `JOB_TYPE_${ConnectProto.JobType[jt]}` as TranslationKey)}
        </OptionRow>
      ))}
    </SearchablePopover>
  );
};

export const CapabilitiesMatrix: React.FC<CapabilitiesMatrixProps> = ({
  ownerOrganizationId,
  selectedJobTypes,
  setSelectedJobTypes,
  selectedContainerTypeIds,
  setSelectedContainerTypeIds,
  capabilitiesMatrix,
  setCapabilitiesMatrix,
}) => {
  const t = useT();
  const { data: containerTypesData } = useListContainerTypesQuery(
    { ownerOrganizationIds: ownerOrganizationId ? [BigInt(ownerOrganizationId)] : [] },
    { skip: !ownerOrganizationId },
  );
  // The list spans the holding: a sister's type is named with its company, so two «Евро 1.1» stay apart.
  const containerTypes = useMemo(
    () =>
      containerTypesData?.containerTypes.map((ct) => {
        const owner = foreignOwnerName(ct, ownerOrganizationId);
        return owner ? { ...ct, name: `${ct.name} (${owner})` } : ct;
      }),
    [containerTypesData, ownerOrganizationId],
  );

  const columns = useMemo<Column<any>[]>(() => {
    const cols: Column<any>[] = [
      {
        id: "container_type",
        header: () => (
          <span className="uppercase text-xs font-bold text-text-primary">
            {t(Namespace.Settings, "truckType.dialog.containerTypeColumn" as TranslationKey)}
          </span>
        ),
        cell: (info: any) => {
          const ctIdRaw = info.row.original;
          if (ctIdRaw === "ADD_ROW") {
            return <AddContainerCell t={t} />;
          }
          const ctIdNum = Number(ctIdRaw);
          const ct = containerTypes?.find((c) => Number(c.id) === ctIdNum);
          return (
            <span className="text-sm text-text-primary text-center w-full">
              {ct?.name || ct?.volume?.toString() || "-"}
            </span>
          );
        },
        width: 150,
        align: "center",
      },
    ];

    selectedJobTypes.forEach((jt) => {
      cols.push({
        id: `job_type_${jt}`,
        header: () => (
          <JobColumnHeader
            jt={jt}
            label={t(Namespace.Enum, `JOB_TYPE_${ConnectProto.JobType[jt]}` as TranslationKey)}
          />
        ),
        cell: (info: any) => {
          const ctIdRaw = info.row.original;
          if (ctIdRaw === "ADD_ROW") return null;
          const ctIdNum = Number(ctIdRaw);
          return <MatrixCheckbox ctIdNum={ctIdNum} jt={jt} />;
        },
        align: "center",
        width: 150,
      });
    });

    cols.push({
      id: "add_job_type",
      header: AddJobHeader,
      cell: () => null,
      align: "center",
      width: 150,
    });

    return cols;
  }, [t, selectedJobTypes, containerTypes]);

  const data = useMemo(() => {
    return [...selectedContainerTypeIds, "ADD_ROW"];
  }, [selectedContainerTypeIds]);

  const dynamicColumnConfig = useMemo(() => {
    return {
      columns: columns.map((c, i) => ({
        id: String(c.id),
        isVisible: true,
        order: i,
        width: typeof c.width === "number" ? c.width : undefined,
      })),
    };
  }, [columns]);

  return (
    <MatrixContext.Provider
      value={{
        capabilitiesMatrix,
        setCapabilitiesMatrix,
        selectedContainerTypeIds,
        setSelectedContainerTypeIds,
        selectedJobTypes,
        setSelectedJobTypes,
        containerTypes,
      }}
    >
      <div
        className="flex border rounded-lg overflow-x-auto overflow-y-hidden flex-col w-fit"
        onContextMenuCapture={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDoubleClickCapture={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <DataTable
          columns={columns}
          data={data}
          rowKey={(row) => String(row)}
          zebra={false}
          columnConfig={dynamicColumnConfig}
          enableColumnReordering={false}
          className="border-0 [&_td:nth-child(1)]:border-r [&_th:nth-child(1)]:border-r"
        />
      </div>
    </MatrixContext.Provider>
  );
};
